import React, { useEffect, useState, useContext } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import Breadcrums from "../components/breadcrums";
import UserContext from "../components/TheUser";
import PageTitle from "../components/pageTitle";
import { nanoid } from "nanoid";
import moment from "moment";
import logger from "../utils/logger";

// Define types
interface Employee {
    uuid: string;
    name: string;
    pid: string;
    p_id: string; // Added p_id property
    p_name: string;
    entity: string; // Added entity property
    start_dt?: string; // Add start date (optional)
    end_dt?: string;   // Add end date (optional)
    active?: number;   // Add active property (optional)
}

type TileProps = {
    date: Date;
    view: "month" | "year" | "decade" | "century";
};

interface PunchedHourEntry {
    date: string; // ISO format "YYYY-MM-DD"
    hours: string;
    employeeId: string | null;
    _d?: Date;
    hrs?: number;
}

interface InvoiceEntry {
    uuid: string;
    pid: string;
    invoice_type: "monthly" | "weekly";
    invoice_month?: number;
    invoice_year?: number;
    invoice_week?: number;
    generated_date: string;
    invoice_no?: string; // Added for freeze logic
}



const breadcrumLinks = [
    { name: "Timesheet", href: "/timesheet", current: false },
    { name: "Master Timesheet", href: "/timesheet/editor/xyz", current: true },
];

export default function TimesheetEditor() {
    const [employees, setEmployees] = useState<Employee[]>([]);
    const { user: contextUser } = useContext(UserContext) || {};
    const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
    const [selectedPid, setSelectedPid] = useState<string | null>(null);
    const [activeStatus, setActiveStatus] = useState<"all" | "active" | "inactive">("all"); // New state for employee status
    
    const [date, setDate] = useState<Date>(new Date());
    const [currentMonth, setCurrentMonth] = useState<{ month: number; year: number }>({
        month: new Date().getMonth() + 1, // JavaScript months are 0-indexed
        year: new Date().getFullYear(),
    });
    const [punchedHours, setPunchedHours] = useState<PunchedHourEntry[]>([
        // { date: "2025-01-01", hours: "8", employeeId: selectedEmployee || null }
    ]);
    const [refreshKey, setRefreshKey] = useState(0); // State to force re-render
    const [tileRefreshTrigger, setTileRefreshTrigger] = useState(0); // State to trigger tile content refresh
    const [employeeStatusFilter, setEmployeeStatusFilter] = useState<"all" | "active" | "inactive">("all");
    const [focusedDate, setFocusedDate] = useState<Date>(new Date());
    const [timezone, setTimezone] = useState<"IST" | "US">("IST"); // Default to IST
    const [invoices, setInvoices] = useState<InvoiceEntry[]>([]);
    const [projectRanges, setProjectRanges] = useState<Array<{ pid: string; start_dt?: string; end_dt?: string }>>([]);
    // State for customized billing cycle (record_st_dt and record_end_dt where index=0 for uuid)
    // Support multiple custom billing cycles for a uuid (multiple index=0 rows)
    const [customBillingCycles, setCustomBillingCycles] = useState<Array<{ record_st_dt: string; record_end_dt: string }>>([]);
    // Fetch customized billing cycle for selected employee (uuid) from project_period where index=0
    useEffect(() => {
        const fetchCustomBillingCycles = async () => {
            if (!selectedEmployee) {
                setCustomBillingCycles([]);
                return;
            }
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/dynamic`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        query: `SELECT record_st_dt, record_end_dt FROM project_period WHERE uuid = '${selectedEmployee}' AND \`index\` = 0`,
                        params: ['project_period'],
                    }),
                });
                if (!response.ok) throw new Error(`Failed to fetch custom billing cycles: ${response.status}`);
                const rows = await response.json();
                if (Array.isArray(rows) && rows.length > 0) {
                    setCustomBillingCycles(rows.map((r: any) => ({ record_st_dt: r.record_st_dt, record_end_dt: r.record_end_dt })));
                } else {
                    setCustomBillingCycles([]);
                }
            } catch (err) {
                logger.error('Error fetching custom billing cycles:', err);
                setCustomBillingCycles([]);
            }
        };
        fetchCustomBillingCycles();
    }, [selectedEmployee]);

    // Auto-detect timezone on mount
    useEffect(() => {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz === "Asia/Kolkata") {
            setTimezone("IST");
        } else {
            setTimezone("US");
        }
    }, []);

    // Helper function to normalize date format as UTC "YYYY-MM-DD"
    const normalizeDate = (date: Date): string => {
        if (!(date instanceof Date) || isNaN(date.getTime())) {
            logger.error("Invalid date provided to normalizeDate:", date);
            return "";
        }
        return moment(date).format("YYYY-MM-DD"); // Use local time, not UTC
    };

    // Helper to convert any input to UTC "YYYY-MM-DD"
    const toYYYYMMDD = (input: Date | string | number): string => {
        const m = moment.utc(input);
        if (!m.isValid()) {
            logger.error("Invalid date:", input);
            return "";
        }
        return m.format("YYYY-MM-DD");
    };

    // Helper function to determine if an employee is active
    const isEmployeeActive = (employee: Employee): boolean => {
        const today = new Date();
        const startDate = employee.start_dt ? new Date(employee.start_dt) : null;
        const endDate = employee.end_dt ? new Date(employee.end_dt) : null;

        return (
            (!startDate || startDate <= today) &&
            (!endDate || endDate >= today)
        );
    };

    // Fetch employees on mount
    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/dynamic`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        query: `SELECT c.uuid, c.active, c.name AS name, c.entity, GROUP_CONCAT(DISTINCT p.pid SEPARATOR ', ') AS p_id, GROUP_CONCAT(DISTINCT pm.name SEPARATOR ', ') AS p_name, MIN(p.start_dt) AS start_dt, MAX(p.end_dt) AS end_dt FROM consultant AS c LEFT JOIN project AS p ON c.uuid = p.uuid AND p.active = true LEFT JOIN project_master AS pm  ON p.pid = pm.pid GROUP BY c.uuid, c.name, c.entity ORDER BY c.name, c.uuid `,
                        params: ['consultant', 'project', 'project_master'],
                    }),
                });
                if (!response.ok) throw new Error("Failed to fetch employees");
                const data: Employee[] = await response.json();
                console.log("Fetched Employees:", data); // Log the fetched employees
                setEmployees(data);

                // Also build projectRanges if the returned rows contain project level start/end (p_id, start_dt/end_dt)
                // If the query returned aggregated p_id, we will fetch project rows separately when an employee is selected.
                // Clear projectRanges for now.
                setProjectRanges([]);
            } catch (error) {
                logger.error("Error fetching employees:", error);
            }
        };
        fetchEmployees();
    }, []);

    // Fetch project ranges when selectedEmployee or month changes
    useEffect(() => {
        const fetchProjectRanges = async () => {
            if (!selectedEmployee) {
                setProjectRanges([]);
                return;
            }
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/dynamic`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        query: `SELECT pid, start_dt, end_dt FROM project WHERE uuid = '${selectedEmployee}'`,
                        params: ['project'],
                    }),
                });
                if (!response.ok) throw new Error('Failed to fetch project ranges');
                const rows = await response.json();
                if (Array.isArray(rows)) {
                    setProjectRanges(rows.map((r: any) => ({ pid: r.pid?.toString?.() || '', start_dt: r.start_dt, end_dt: r.end_dt })));
                } else {
                    setProjectRanges([]);
                }
            } catch (err) {
                logger.error('Error fetching project ranges:', err);
                setProjectRanges([]);
            }
        };
        fetchProjectRanges();
    }, [selectedEmployee, currentMonth]);

    // Fetch invoices when employee or month changes
    useEffect(() => {
        const fetchInvoices = async () => {
            if (!selectedEmployee || !selectedPid) return;
            try {
                console.log("Fetching invoices for:", { selectedEmployee, selectedPid, currentMonth });
                
                // Calculate a wider date range (extend by 7 days each side) to ensure weekly invoices that span month boundaries are included
                const monthStart = new Date(currentMonth.year, currentMonth.month - 1, 1);
                const monthEnd = new Date(currentMonth.year, currentMonth.month, 0); // last day of the month
                const rangeStart = moment(monthStart).subtract(7, 'days').format('YYYY-MM-DD');
                const rangeEnd = moment(monthEnd).add(7, 'days').format('YYYY-MM-DD');

                const response = await fetch(`${process.env.REACT_APP_API_URL}/dynamic`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                // Expand to a date range around the month to include weekly invoices that might fall outside strict month boundaries
                query: `SELECT DISTINCT f.uuid, f.pid, 
                    p.billing_period as invoice_type,
                    MONTH(f._d) as invoice_month, 
                    YEAR(f._d) as invoice_year, 
                    WEEK(f._d, 4) as invoice_week, 
                    MIN(f._d) as generated_date,
                    f.invoice_no
                    FROM finance f
                    JOIN project p ON f.pid = p.pid
                    WHERE f.uuid = '${selectedEmployee}' 
                    AND f.pid IN (${selectedPid})
                    AND f.invoice_no IS NOT NULL 
                    AND f.invoice_no != '' 
                    AND f.invoice_no != '0' 
                    AND f.invoice_no != 'null'
                    AND TRIM(f.invoice_no) != ''
                    AND f._d BETWEEN '${rangeStart}' AND '${rangeEnd}'
                    GROUP BY f.uuid, f.pid, MONTH(f._d), YEAR(f._d), WEEK(f._d, 4), p.billing_period, f.invoice_no`,
                params: ['finance'],
                }),
                });
                
                console.log("Invoice fetch response status:", response.status);
                
                if (!response.ok) {
                    console.warn("Invoice fetch failed, trying alternative queries");
                    
                    // First try: Check for invoice_no in finance table (comprehensive check)
                    const financeInvoiceResponse = await fetch(`${process.env.REACT_APP_API_URL}/dynamic`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            query: `SELECT uuid, pid, 'monthly' as invoice_type, ${currentMonth.month} as invoice_month, ${currentMonth.year} as invoice_year, 0 as invoice_week, MIN(_d) as generated_date, invoice_no,
                                           COUNT(*) as record_count
                                    FROM finance 
                                    WHERE uuid = '${selectedEmployee}' 
                                    AND pid IN (${selectedPid})
                                    AND (
                                        (invoice_no IS NOT NULL AND invoice_no != '' AND invoice_no != '0' AND invoice_no != 'null')
                                        OR 
                                        (TRIM(COALESCE(invoice_no, '')) != '')
                                    )
                                    AND MONTH(_d) = ${currentMonth.month} 
                                    AND YEAR(_d) = ${currentMonth.year}
                                    GROUP BY uuid, pid, invoice_no
                                    HAVING COUNT(*) > 0`,
                            params: ['finance'],
                        }),
                    });
                    
                    console.log("Checking finance table for invoice_no...");
                    if (financeInvoiceResponse.ok) {
                        const invoiceData = await financeInvoiceResponse.json();
                        console.log("Invoice check response:", invoiceData);
                        if (invoiceData && invoiceData.length > 0) {
                            console.log("Found invoices by invoice_no:", invoiceData);
                            setInvoices(invoiceData);
                            return;
                        } else {
                            console.log("No invoice numbers found in finance table");
                        }
                    } else {
                        logger.error("Finance invoice query failed:", financeInvoiceResponse.status);
                    }
                    
                    // Second try: Check for billing/payment status
                    const alternativeResponse = await fetch(`${process.env.REACT_APP_API_URL}/dynamic`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            query: `SELECT uuid, pid, 'monthly' as invoice_type, ${currentMonth.month} as invoice_month, ${currentMonth.year} as invoice_year, 0 as invoice_week, MIN(_d) as generated_date 
                                    FROM finance 
                                    WHERE uuid = '${selectedEmployee}' 
                                    AND pid IN (${selectedPid})
                                    AND (paid > 0 OR payment > 0)
                                    AND MONTH(_d) = ${currentMonth.month} 
                                    AND YEAR(_d) = ${currentMonth.year}
                                    GROUP BY uuid, pid
                                    HAVING COUNT(*) > 0`,
                            params: ['finance'],
                        }),
                    });
                    
                    if (alternativeResponse.ok) {
                        const data: InvoiceEntry[] = await alternativeResponse.json();
                        console.log("Fetched invoices (alternative):", data);
                        setInvoices(data);
                        return;
                    }
                    
                    throw new Error("Failed to fetch invoices from both sources");
                }
                
                const data: InvoiceEntry[] = await response.json();
                console.log("Fetched invoices (filtered to current month): count=", data?.length, data?.slice(0,5));
                setInvoices(data || []);
            } catch (error) {
                logger.error("Error fetching invoices:", error);
                setInvoices([]);
            }
        };
        fetchInvoices();
    }, [selectedEmployee, selectedPid, currentMonth]);

    // Fetch punched hours when employee or month changes
    useEffect(() => {
        const fetchPunchedHours = async () => {
            if (!selectedEmployee) return;
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/dynamic`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        // query: "SELECT  hrs,DAY(_d) as day,WEEK(_d,4) as week, _d , uuid, pid, hrs, verified from finance where  " + " MONTH(_d) =  " + currentMonth.month + " AND YEAR(_d) = " + currentMonth.year + " and uuid ='" + selectedEmployee + "'  ",
query: "SELECT f.hrs, DAY(f._d) AS day, WEEK(f._d, 4) AS week, f._d, f.uuid, f.pid, f.verified, COUNT(*) AS entry_count, MIN(p.start_dt) AS start_dt, MAX(p.end_dt) AS end_dt " + "FROM finance f " + "JOIN project p ON f.pid = p.pid " + "WHERE MONTH(f._d) = " + currentMonth.month + " AND YEAR(f._d) = " + currentMonth.year + " AND f.uuid = '" + selectedEmployee + "' " + "GROUP BY f._d, f.hrs, f.uuid, f.pid, f.verified " + "ORDER BY f._d, f.hrs",
                        //+ " and pid = '" + 1 + "'"
                        params: ["finance"],
                     }),
                });

                console.log(response);

                if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

                if (response.ok) {
                    const data: PunchedHourEntry[] = await response.json();
                    console.info(data);

                    if (!Array.isArray(data)) throw new Error("Invalid data format received from API");

                    setPunchedHours(
                        data.map((entry) => {
                            // const parsedDate = entry?._d ? new Date(entry._d) : null;
                            // const correctedDate = parsedDate; // Adjust for timezone offset
                            //+ parsedDate.getTimezoneOffset() * 60000
                            // correctedDate ? toYYYYMMDD(correctedDate) : "", // Use corrected date
                            return {
                                ...entry,
                                date: entry?._d ? toYYYYMMDD(entry._d) : "",
                                employeeId: selectedEmployee,
                                hours: entry?.hrs?.toString() || "0",
                            };
                        })
                    );

                    console.info("Punched hours fetched successfully:", punchedHours);
                }
            } catch (error) {
                logger.error("Error fetching punched hours:", error);
                setPunchedHours([]);
            }
        };
        fetchPunchedHours();
    }, [selectedEmployee, currentMonth]);

    const handleDateClick = (date: Date, moveNext = false) => {
        const formattedDate = normalizeDate(date); // Format date as "YYYY-MM-DD"

        // Find the correct project for this date from projectRanges
        type ProjectRange = { pid: string; start_dt?: string; end_dt?: string };
        let matchedProject: ProjectRange | null = null;
        if (projectRanges && projectRanges.length > 0) {
            const dateToCheck = new Date(date.getFullYear(), date.getMonth(), date.getDate());
            const found = projectRanges.find((range: ProjectRange) => {
                if (!range.start_dt) return false;
                const start = new Date(range.start_dt);
                const normalizedStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                if (dateToCheck < normalizedStart) return false;
                if (!range.end_dt) return true;
                const endDateStr = moment(range.end_dt).format('YYYY-MM-DD');
                if (endDateStr === '9999-12-31') return true; // Ongoing sentinel
                const end = new Date(range.end_dt);
                const normalizedEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());
                return dateToCheck <= normalizedEnd;
            });
            if (found) matchedProject = found;
        }

        if (!matchedProject || !matchedProject.pid) {
            alert("No project is mapped to the selected employee for this date. Please check project assignments.");
            return;
        }

        const newHours = prompt(`Enter punched hours for ${formattedDate}`, "8");

        if (!newHours || isNaN(Number(newHours))) {
            alert("Invalid hours entered. Please try again.");
            return;
        }

        const updatePunchedHours = async () => {
            console.info("Updating punched hours...");
            // determine current user for audit fields
            let currentUser = "system";
            try {
                if (contextUser) {
                    currentUser = contextUser.name || contextUser.email || currentUser;
                }
                console.log("Current User for audit fields:", currentUser);
            } catch (e) {
                // fallback to system
                console.warn("Failed to get current user from context, using 'system'");
            }

            const payload = {
                uuid: selectedEmployee,
                pid: matchedProject!.pid, // Use only the matched project id (non-null assertion)
                _d: formattedDate,
                paid: 0,
                hrs: newHours,
                _ts_bill: normalizeDate(new Date()),
                payment: 0,
                verified: true,
                created_by: currentUser,
                updated_by: currentUser,
                operation: "timesheet"
            };
            console.log("Payload being sent to API:", payload);
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/finance`, {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                });
                if (!response.ok) {
                    const errorText = await response.text();
                    logger.error("API Error:", response.status, errorText);
                    alert(`Failed to update punched hours: ${errorText}`);
                    return;
                }
                // Update punched hours locally
                const existingEntryIndex = punchedHours.findIndex((entry) => entry.date === formattedDate);
                if (existingEntryIndex !== -1) {
                    setPunchedHours((prev) =>
                        prev.map((entry, idx) =>
                            idx === existingEntryIndex
                                ? { ...entry, hours: newHours }
                                : entry
                        )
                    );
                } else {
                    setPunchedHours((prev) => [
                        ...prev,
                        { date: formattedDate, hours: newHours, employeeId: selectedEmployee },
                    ]);
                }
                setTileRefreshTrigger((prev) => prev + 1); // Trigger tile content refresh
                // Move to next cell if requested
                if (moveNext) {
                    const nextDate = new Date(date);
                    nextDate.setDate(nextDate.getDate() + 1);
                    setFocusedDate(nextDate);
                    setDate(nextDate);
                }
            } catch (error) {
                logger.error("Error updating punched hours:", error);
                alert("An error occurred while updating punched hours. Please try again.");
            }
        };
        updatePunchedHours();
        setDate(date);
    };

    // Keyboard navigation handler
    useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
            if (document.activeElement?.tagName === "INPUT" || document.activeElement?.tagName === "SELECT") return;
            let newDate = new Date(focusedDate);
            switch (e.key) {
                case "ArrowLeft":
                    newDate.setDate(newDate.getDate() - 1);
                    setFocusedDate(newDate);
                    setDate(newDate);
                    break;
                case "ArrowRight":
                    newDate.setDate(newDate.getDate() + 1);
                    setFocusedDate(newDate);
                    setDate(newDate);
                    break;
                case "ArrowUp":
                    newDate.setDate(newDate.getDate() - 7);
                    setFocusedDate(newDate);
                    setDate(newDate);
                    break;
                case "ArrowDown":
                    newDate.setDate(newDate.getDate() + 7);
                    setFocusedDate(newDate);
                    setDate(newDate);
                    break;
                case "Enter":
                    // If the month is fully invoiced (monthly), prevent editing via Enter
                    if (isMonthFullyInvoiced) {
                        alert("Timesheet is frozen: a monthly invoice has been generated for this period.");
                    } else {
                        handleDateClick(focusedDate, true); // Move to next cell after entry
                    }
                    break;
                default:
                    break;
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [focusedDate, handleDateClick]);

    // Determine whether the current month is fully invoiced (monthly) for the selected employee/pid
    const isMonthFullyInvoiced = React.useMemo(() => {
        if (!selectedEmployee || !selectedPid || !invoices || invoices.length === 0) return false;
        try {
            const activePids = selectedPid ? selectedPid.toString().split(',').map(s => s.trim()).filter(Boolean) : [];
            return invoices.some((inv) => {
                // Normalize invoice type and allow variants like 'Monthly Itemized'
                const invoiceType = (inv.invoice_type || "").toString().toLowerCase();
                if (!invoiceType.includes('month')) return false;

                // Ensure invoice row belongs to selected employee and pid(s)
                const invUuid = inv.uuid !== undefined && inv.uuid !== null ? inv.uuid.toString() : '';
                const invPid = inv.pid !== undefined && inv.pid !== null ? inv.pid.toString() : '';
                if (invUuid !== selectedEmployee) return false;
                if (activePids.length > 0 && !activePids.includes(invPid)) return false;

                const invMonth = inv.invoice_month !== undefined && inv.invoice_month !== null ? Number(inv.invoice_month) : NaN;
                const invYear = inv.invoice_year !== undefined && inv.invoice_year !== null ? Number(inv.invoice_year) : NaN;
                return !isNaN(invMonth) && !isNaN(invYear) && invMonth === currentMonth.month && invYear === currentMonth.year;
            });
        } catch (err) {
            console.warn('Error computing isMonthFullyInvoiced', err);
            return false;
        }
    }, [invoices, selectedEmployee, selectedPid, currentMonth]);

    // Highlight focused cell
    const renderTileContent = React.useCallback(
        ({ date, view }: TileProps) => {
            if (view === "month") {
                // Format as local date string to match calendar tile and backend
                const formattedTileDate = moment(date).format("YYYY-MM-DD");

                const punchedHour = punchedHours.find(
                    (entry) => entry.date === formattedTileDate
                );
                const isFocused =
                    date.toDateString() === focusedDate.toDateString();
                
                // Check if the date is disabled
                const isDisabled = renderTileDisabled({ date, view });
                
                return (
                    <div
                        style={{
                            fontSize: "12px",
                            textAlign: "center",
                            color: isDisabled ? "#d1d5db" : "gray",
                            background: isFocused ? "#e0e7ff" : undefined,
                            borderRadius: isFocused ? "50%" : undefined,
                            opacity: isDisabled ? 0.5 : 1,
                        }}
                    >
                        {punchedHour && punchedHour.hours && punchedHour.hours !== "0"
                            ? `${punchedHour.hours}h`
                            : ""}
                    </div>
                );
            }
            return null;
        },
        [punchedHours, tileRefreshTrigger, focusedDate, timezone, selectedEmployee, employees, invoices]
    );


    const renderTileDisabled = ({ date, view }: TileProps): boolean => {
        if (view !== "month" || !selectedEmployee) return false;

        // If any custom billing cycle exists, freeze all such periods ONLY if an invoice exists in that period for the user
        if (customBillingCycles.length > 0) {
            const checkDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
            for (const cycle of customBillingCycles) {
                if (cycle.record_st_dt && cycle.record_end_dt) {
                    const freezeStart = new Date(cycle.record_st_dt);
                    const freezeEnd = new Date(cycle.record_end_dt);
                    // Check if any invoice exists in this period for the user
                    const hasInvoiceInPeriod = invoices.some(inv => {
                        // Only consider invoices for this user
                        if (inv.uuid !== selectedEmployee) return false;
                        // Invoice date: use generated_date (MIN(_d))
                        const invDate = inv.generated_date ? new Date(inv.generated_date) : null;
                        if (!invDate) return false;
                        // Invoice must have a non-empty invoice_no
                        if (!inv.invoice_no || inv.invoice_no === '0' || inv.invoice_no === 'null' || inv.invoice_no === '') return false;
                        return invDate >= freezeStart && invDate <= freezeEnd;
                    });
                    // Freeze the entire range only if invoice exists in this period
                    if (hasInvoiceInPeriod && checkDate >= freezeStart && checkDate <= freezeEnd) {
                        return true;
                    }
                    // Always freeze the first day of the cycle if invoice exists in this period
                    if (hasInvoiceInPeriod &&
                        checkDate.getFullYear() === freezeStart.getFullYear() &&
                        checkDate.getMonth() === freezeStart.getMonth() &&
                        checkDate.getDate() === freezeStart.getDate()
                    ) {
                        return true;
                    }
                }
            }
        }

        // If entire month is invoiced as monthly, disable all tiles in that month (unless custom cycle is present)
    if (isMonthFullyInvoiced && customBillingCycles.length === 0) {
            const dateMonth = date.getMonth() + 1;
            const dateYear = date.getFullYear();
            if (dateMonth === currentMonth.month && dateYear === currentMonth.year) {
                return true;
            }
        }

        // Check if invoice has been generated for this date based on billing period
        // Use ISO week (week of year) to avoid locale differences and ensure week boundaries match backend
        const dateWeekIso = moment(date).isoWeek();
        const dateMonth = date.getMonth() + 1;
        const dateYear = date.getFullYear();

        console.log("Checking tile disabled for date:", {
            date: moment(date).format('YYYY-MM-DD'),
            dateWeekIso,
            dateMonth,
            dateYear,
            invoicesCount: invoices.length,
        });

    // Check if any invoice exists that would disable this date
    // Only consider invoices that belong to selected employee/pid and have a valid invoice type
    const activePids = selectedPid ? selectedPid.toString().split(',').map(s => s.trim()).filter(Boolean) : [];
    const blockingInvoice = invoices.find((invoice) => {
            try {
        // Ensure invoice belongs to selected employee/pid
        const invUuid = invoice.uuid !== undefined && invoice.uuid !== null ? invoice.uuid.toString() : '';
        const invPid = invoice.pid !== undefined && invoice.pid !== null ? invoice.pid.toString() : '';
        if (invUuid !== selectedEmployee) return false;
        if (activePids.length > 0 && !activePids.includes(invPid)) return false;

        // Normalize invoice fields into numbers where appropriate
        const invoiceType = (invoice.invoice_type || "").toString().toLowerCase();

                // Prefer explicit invoice_week/invoice_year when provided. If missing, fall back to generated_date (MIN(_d))
                let invWeek = invoice.invoice_week !== undefined && invoice.invoice_week !== null ? Number(invoice.invoice_week) : NaN;
                let invYear = invoice.invoice_year !== undefined && invoice.invoice_year !== null ? Number(invoice.invoice_year) : NaN;
                const invMonth = invoice.invoice_month !== undefined && invoice.invoice_month !== null ? Number(invoice.invoice_month) : NaN;

                // If week/year are not provided but generated_date exists, derive ISO week/year from it
                if ((isNaN(invWeek) || isNaN(invYear)) && (invoice as any).generated_date) {
                    const d = new Date((invoice as any).generated_date);
                    if (!isNaN(d.getTime())) {
                        invWeek = moment(d).isoWeek();
                        invYear = moment(d).isoWeekYear();
                    }
                }

                console.log("Comparing invoice:", { invoiceType, invYear, invMonth, invWeek }, "with date:", { dateWeekIso, dateMonth, dateYear });

                if (invoiceType.includes('month')) {
                    // Only match when both month and year are valid numbers
                    const match = !isNaN(invMonth) && !isNaN(invYear) && invMonth === dateMonth && invYear === dateYear;
                    if (match) console.log('Monthly invoice disables date', invoice);
                    return match;
                }

                if (invoiceType === 'weekly') {
                    // Use ISO week + ISO week-year comparison. This ensures weeks that span months/years map correctly (e.g., Dec 28 - Jan 3)
                    const match = !isNaN(invWeek) && !isNaN(invYear) && invWeek === dateWeekIso && invYear === moment(date).isoWeekYear();
                    if (match) console.log('Weekly invoice disables date', invoice);
                    return match;
                }

                return false;
            } catch (err) {
                console.warn('Error while comparing invoice for tileDisabled:', err, invoice);
                return false;
            }
        });

        console.log('Final blockingInvoice:', blockingInvoice);
        if (blockingInvoice) {
            console.log('Tile disabled because of invoice:', blockingInvoice);
            return true;
        }

        // If projectRanges has entries, consider any of them to allow date
        const dateToCheck = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        if (projectRanges && projectRanges.length > 0) {
            // If any project range contains the date, tile should be enabled
            const isInAnyRange = projectRanges.some(range => {
                if (!range.start_dt) return false;
                const start = new Date(range.start_dt);
                const normalizedStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                if (dateToCheck < normalizedStart) return false;
                if (!range.end_dt) return true;
                const endDateStr = moment(range.end_dt).format('YYYY-MM-DD');
                if (endDateStr === '9999-12-31') return true; // Ongoing sentinel
                const end = new Date(range.end_dt);
                const normalizedEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());
                return dateToCheck <= normalizedEnd;
            });

            console.log('Project ranges debug:', { selectedEmployee, projectRanges, dateToCheck, isInAnyRange });
            // If not in any active project range, disable
            return !isInAnyRange;
        }

        // Fallback: use employee-level start/end if present
        const selectedEmployeeData = employees.find((employee) => employee.uuid === selectedEmployee);
        if (!selectedEmployeeData) return false;

        if (!selectedEmployeeData.start_dt || !selectedEmployeeData.end_dt) return false;

        const startDate = new Date(selectedEmployeeData.start_dt);
        const endDate = new Date(selectedEmployeeData.end_dt);
        const normalizedStartDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const normalizedEndDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

        console.log('Project duration debug (fallback):', { selectedEmployee, normalizedStartDate, normalizedEndDate, dateToCheck });

        return dateToCheck < normalizedStartDate || dateToCheck > normalizedEndDate;
    };

    const handleNavigation = (e) => {
        // console.log(e);
        // console.log(e.action);
        // console.log(e.activeStartDate);

        // Selected User
        console.log(selectedEmployee);

        //  Update View - Month-Year reference
        setCurrentMonth({
            month: new Date(e.activeStartDate).getMonth() + 1, // JavaScript months are 0-indexed
            year: new Date(e.activeStartDate).getFullYear(),
        });

        // Load Finance APIs for selected range and UUID
        setDate(new Date(e.activeStartDate)); // Set the date to the first day of the month
    };

    const totalHours = React.useMemo(() => {
        // Deduplicate by date, keeping the last entry for each date
        const map = new Map<string, PunchedHourEntry>();
        punchedHours
            .filter(entry => entry.employeeId === selectedEmployee)
            .forEach(entry => map.set(entry.date, entry));
        return Array.from(map.values()).reduce((total, entry) => {
            const h = parseFloat(entry.hours || "0");
            return !isNaN(h) ? total + h : total;
        }, 0);
    }, [punchedHours, selectedEmployee]);

    return (
        <main>
            <Breadcrums breadcrumLinks={breadcrumLinks} module={"Dashboard"} />
            <PageTitle title="Master Timesheet" preSummary="Operational Master Timesheet" summary="Administrator Console"></PageTitle>

            <section className="px-4 py-6 sm:px-6 lg:px-8">
                {/* Dropdown to filter employees by status */}
                <label>Filter Employees:</label>
                <select
                    onChange={(e) => setActiveStatus(e.target.value as "all" | "active" | "inactive")}
                    value={activeStatus}
                    className="block px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm w-96 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                    <option value="all">All Employees</option>
                    <option value="active">Active Employees</option>
                    <option value="inactive">Inactive Employees</option>
                </select>

                <label className="mt-4 block">Select Employee:</label>
                <select
                    onChange={(e) => {
                        const selectedUuid = e.target.value;
                        const selectedEmployeeData = employees.find((employee) => employee.uuid === selectedUuid);
                        setSelectedEmployee(selectedUuid);
                        setSelectedPid(selectedEmployeeData?.p_id || null); // Dynamically set the pid
                        console.log("Selected Employee:", selectedEmployeeData);
                    }}
                    value={selectedEmployee || ""}
                    className="block px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm w-96 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                    <option value="">Select Employee</option>
                    {employees
                        .filter((employee) => {
                            // Filter employees based on activeStatus
                            if (activeStatus === "active") return employee?.active == 1 && employee.entity === 'MTINC';
                            if (activeStatus === "inactive") return employee?.active == 0 && employee.entity === 'MTINC';
                            return employee.entity === 'MTINC'; // Show all employees for "all"
                        })
                        .map((employee) => (
                            <option
                                key={employee.uuid}
                                value={employee.uuid}
                                data-uuid={employee.uuid}
                                data-pid={employee.pid}
                                data-p_name={employee.p_name}
                            >
                                {employee.name} 
                            </option>
                        ))}
                </select>
                {/* <h3 className="pt-0 text-xs text-gray-500">{selectedEmployee || ""}</h3> */}
            </section>

            <section className="px-4 py-6 sm:px-6 lg:px-8">
                {/* Display Total Hours above the calendar */}
                <div className="mb-4">
                    <p className="text-sm font-semibold">
                        Total Hours: {totalHours} hours
                    </p>
                </div>

                <div
                    style={{
                        transform: "scale(1.5)", // Adjusted scale to make the calendar fit better
                        transformOrigin: "top left",
                        overflow: "auto", // Enable scrolling
                        maxHeight: "calc(100vh - 380px)", // Set a maximum height for the scrollable area
                        marginBottom: "40px", // Increased space below the calendar
                    }}
                >
                    <style>{`
                        .react-calendar__tile:disabled {
                            background-color: #f3f4f6 !important;
                            color: #d1d5db !important;
                            cursor: not-allowed !important;
                        }
                        .react-calendar__tile:disabled:hover {
                            background-color: #f3f4f6 !important;
                        }
                        .react-calendar__month-view__days__day--neighboringMonth {
                            visibility: hidden !important;
                        }
                    `}</style>
                    <Calendar
                        onChange={(newDate) => {
                            setDate(newDate as Date);
                            setFocusedDate(newDate as Date);
                        }}
                        onActiveStartDateChange={(e) => {
                            handleNavigation(e);
                        }}
                        onViewChange={() => {}}
                        onClickDay={(date) => {
                            setFocusedDate(date);
                            if (isMonthFullyInvoiced) {
                                alert("Timesheet is frozen: a monthly invoice has been generated for this period.");
                                return;
                            }
                            handleDateClick(date);
                        }}
                        value={date}
                        key={tileRefreshTrigger}
                        tileContent={renderTileContent}
                        tileDisabled={renderTileDisabled}
                        className={"rounded-lg border border-gray-0 shadow-sm"}
                    />
                </div>

                {/* Add more space below the calendar */}
                <div style={{ marginTop: "40px" }}></div>
            </section>
        </main>
    );
}
